import type {PartialPickup} from '../work/partial-pickup-schema'
import {
    computeAllocationStatus,
    formatAllocationNames,
    inferRequirementsFromAllocations,
} from './allocation'
import {toGlobalSlotIndexOrLast} from './timeline-view'
import type {
    AllocationStatus,
    DispatchJobSchedule,
    DispatchResource,
    DispatchTimelineData,
    JobAllocation,
    JobRequirement,
    JobScheduleKind,
    UnassignedJob,
} from './types'

export type TimelineJobRow = {
    id: string
    jobCode: string
    label: string
    customer: string
    internalContactName?: string | null
    closerYardName?: string | null
    outOfStateLabel?: string | null
    /** Set when `dispatchYardId` is null. */
    noYard?: boolean
    partialPickup?: PartialPickup | null
    /** Rentals put on rent automatically that dispatch has not confirmed went out. */
    unconfirmedRentalCount?: number
    span?: DispatchTimelineData['assignments'][number]['span']
    schedule?: DispatchJobSchedule
    scheduleKind: JobScheduleKind
    warnings?: string[]
    status: 'unassigned' | 'assigned'
    driverId: string | null
    allocations: JobAllocation[]
    allocationStatus?: AllocationStatus
    requirementSummaries?: string[]
    requirements?: JobRequirement[]
    /** Persisted job id — set on committed rows via assignment.sourceJobId. */
    sourceJobId?: string
    /** Present only for unassigned rows — used to open the allocate modal. */
    unassignedJob?: UnassignedJob
    boardScenario?: UnassignedJob['boardScenario']
}

export type JobRowGroups = {
    unassigned: TimelineJobRow[]
    scheduled: TimelineJobRow[]
}

export function timelineRowFromUnassignedJob(job: UnassignedJob): TimelineJobRow {
    return toRowFromUnassigned(job)
}

/** Resolve a board job id to a timeline row for drawer replace navigation. */
export function timelineRowForBoardJobId(
    data: DispatchTimelineData,
    jobId: string,
): TimelineJobRow | null {
    const unassigned = data.unassignedJobs.find((job) => job.id === jobId)
    if (unassigned) {
        return toRowFromUnassigned(unassigned)
    }

    const assignment = data.assignments.find(
        (entry) => entry.sourceJobId === jobId || entry.id === jobId,
    )
    if (assignment) {
        return toRowFromAssignment(assignment)
    }

    return null
}

function toRowFromUnassigned(job: UnassignedJob): TimelineJobRow {
    return {
        id: job.id,
        jobCode: job.jobCode,
        label: job.label,
        customer: job.customer,
        internalContactName: job.internalContactName,
        ...(job.closerYardName ? {closerYardName: job.closerYardName} : {}),
        ...(job.outOfStateLabel ? {outOfStateLabel: job.outOfStateLabel} : {}),
        ...(job.dispatchYardId === null ? {noYard: true} : {}),
        ...(job.partialPickup ? {partialPickup: job.partialPickup} : {}),
        ...(job.unconfirmedRentalCount ? {unconfirmedRentalCount: job.unconfirmedRentalCount} : {}),
        span: job.span,
        schedule: job.schedule,
        scheduleKind: job.scheduleKind,
        warnings: job.warnings,
        status: 'unassigned',
        driverId: null,
        allocations: job.allocations,
        allocationStatus: computeAllocationStatus(job),
        sourceJobId: job.id,
        unassignedJob: job,
        boardScenario: job.boardScenario,
    }
}

function toRowFromAssignment(
    assignment: DispatchTimelineData['assignments'][number],
): TimelineJobRow {
    return {
        id: assignment.id,
        jobCode: assignment.jobCode,
        label: assignment.label,
        customer: assignment.customer,
        internalContactName: assignment.internalContactName,
        ...(assignment.closerYardName ? {closerYardName: assignment.closerYardName} : {}),
        ...(assignment.outOfStateLabel ? {outOfStateLabel: assignment.outOfStateLabel} : {}),
        ...(assignment.dispatchYardId === null ? {noYard: true} : {}),
        ...(assignment.partialPickup ? {partialPickup: assignment.partialPickup} : {}),
        ...(assignment.unconfirmedRentalCount
            ? {unconfirmedRentalCount: assignment.unconfirmedRentalCount}
            : {}),
        span: assignment.span,
        schedule: assignment.schedule,
        scheduleKind: assignment.scheduleKind,
        warnings: assignment.warnings,
        status: 'assigned',
        driverId: assignment.resourceId,
        allocations: assignment.allocations ?? [],
        requirements: assignment.requirements,
        allocationStatus: assignment.allocationStatus,
        sourceJobId: assignment.sourceJobId,
        boardScenario: assignment.boardScenario,
    }
}

export function buildJobRows(data: DispatchTimelineData): JobRowGroups {
    const unassigned = data.unassignedJobs
        .map((job) => toRowFromUnassigned(job))
        .sort((a, b) => toGlobalSlotIndexOrLast(a.span) - toGlobalSlotIndexOrLast(b.span))

    const scheduled = data.assignments
        .map((assignment) => toRowFromAssignment(assignment))
        .sort((a, b) => toGlobalSlotIndexOrLast(a.span) - toGlobalSlotIndexOrLast(b.span))

    return {unassigned, scheduled}
}

/** All jobs on one board — attention jobs interleaved by schedule order. */
export function buildUnifiedJobRows(data: DispatchTimelineData): TimelineJobRow[] {
    const {unassigned, scheduled} = buildJobRows(data)
    return [...unassigned, ...scheduled].sort(
        (a, b) => toGlobalSlotIndexOrLast(a.span) - toGlobalSlotIndexOrLast(b.span),
    )
}

export function findResource(
    resources: DispatchResource[],
    driverId: string | null,
): DispatchResource | undefined {
    if (!driverId) return undefined
    return resources.find((resource) => resource.id === driverId)
}

export function resourceLabelForRow(row: TimelineJobRow): string | null {
    if (row.allocations.length > 0) {
        return formatAllocationNames(row.allocations)
    }
    return null
}

export function resolveJobRequirements(job: TimelineJobRow): JobRequirement[] {
    if (job.unassignedJob?.requirements.length) {
        return job.unassignedJob.requirements
    }
    if (job.requirements?.length) {
        return job.requirements
    }
    return inferRequirementsFromAllocations(job.allocations)
}
